import { podeFiscalizar } from '../../auth/permissoes'
import { useSessao } from '../../auth/contexto'
import { useGerarResumo } from '../../api/queries/repositorios'
import { Botao } from '../../components/Botao'
import { AvisoDeErro, Carregando } from '../../components/Estados'
import { SeloDeMaquina } from '../../components/SeloDeMaquina'
import { dataHora, dataSimples } from '../../lib/datas'
import { TEXTOS } from '../../lib/glossario'
import type { ApoioDoResumo } from '../../types/repositorios'
import { CorpoDoResumo } from './PontosDeAtencao'
import { rotuloDoEstado } from './setores'

/**
 * O resumo do mês escrito pela IA, e o estado em que ele está.
 *
 * O estado manda na tela: "preparando" mostra o texto anterior com a espera por cima; "entrou
 * commit depois" mostra o texto com a ressalva; "no teto" e "sem crédito" explicam por que não há
 * botão. O botão é só do fiscal e só quando o servidor disse que ele faz sentido — oferecer um
 * botão cuja resposta é 403 é o erro da divergência 9 do roteiro de UAT.
 *
 * @param props o apoio como o servidor o devolveu e o mês
 * @returns o cartão do resumo
 */
export function ResumoDaIa({ apoio, competencia }: { apoio: ApoioDoResumo; competencia: string }) {
  const t = TEXTOS.repositorios.resumo
  const { usuario } = useSessao()
  const gerar = useGerarResumo(competencia)
  const resumo = apoio.resumo
  const conteudo = resumo?.conteudo
  const mostraBotao = apoio.podePedir && podeFiscalizar(usuario)
  const preparando = apoio.estado === 'PREPARANDO' || gerar.isPending

  return (
    <section className="cartao space-y-4 p-5" aria-label={t.titulo}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{t.titulo}</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">{t.ajuda}</p>
        </div>
        {mostraBotao && (
          <Botao aparencia="secundario" tamanho="pequeno" onClick={() => gerar.mutate()} disabled={preparando}>
            {preparando ? t.gerando : resumo ? t.refazer : t.botao}
          </Botao>
        )}
      </header>

      <p className="text-sm font-medium text-slate-700">
        {rotuloDoEstado(apoio.estado)}
        {resumo && (
          <span className="font-normal text-slate-500">
            {' '}
            — {t.cobertura(dataSimples(resumo.ateDia), dataHora(resumo.concluidoEm ?? resumo.geradoEm))}
            {resumo.mesFechado && ` ${t.definitivo}`}
          </span>
        )}
      </p>

      {apoio.estado === 'DESATUALIZADO' && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-slate-800">{t.desatualizadoAjuda}</p>
      )}
      {apoio.estado === 'NUNCA_GERADO' && <p className="text-sm text-slate-600">{t.nuncaGeradoAjuda}</p>}
      {apoio.motivo && (apoio.estado === 'FALHOU' || apoio.estado === 'INDISPONIVEL' || apoio.estado === 'NO_TETO' || apoio.estado === 'SEM_CREDITO') && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-slate-800">{apoio.motivo}</p>
      )}
      {gerar.isError && <AvisoDeErro erro={gerar.error} titulo={t.falhou} />}
      {gerar.data?.estado === 'FALHOU' && gerar.data.motivo && <AvisoDeErro erro={new Error(gerar.data.motivo)} titulo={t.falhou} />}
      {preparando && <Carregando texto={t.gerando} />}

      {conteudo && <CorpoDoResumo conteudo={conteudo} />}
      {resumo && <SeloDeMaquina modelo={resumo.modelo ?? null} quando={resumo.concluidoEm ?? null} />}
    </section>
  )
}
